import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { NAV_SPOTLIGHT } from '../config/season';

const PRIMARY_ITEMS = [
  { to: '/', label: 'HOME' },
  { to: '/ai-picks', label: 'AI PICKS' },
  { to: '/draft-room', label: 'DRAFT ROOM' },
  { to: '/draft-grades', label: 'DRAFT GRADES' },
  { to: '/the-tape', label: 'THE TAPE' },
  { to: '/managers', label: 'MANAGERS' },
];

const MORE_ITEMS = [
  { to: '/champions', label: 'CHAMPIONS' },
  { to: '/bureau', label: 'BUREAU' },
  { to: '/stats', label: 'STATS' },
  { to: '/keeper-vault', label: 'KEEPER VAULT' },
  { to: '/archive', label: 'ARCHIVE' },
];

const ALL_ITEMS = [...PRIMARY_ITEMS, ...MORE_ITEMS];
const isSpotlight = (to) => to === NAV_SPOTLIGHT.to;
const routeMatches = (pathname, to) => pathname === to || (to !== '/' && pathname.startsWith(`${to}/`));

// Sticky chrome. Transparent over the top of a page (so the film and heroes own the frame), solid once you scroll.
// Mobile: a full-screen sheet instead of eleven wrapping links.
export default function TopNav() {
  const [solid, setSolid] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const moreButtonRef = useRef(null);
  const moreDisclosureRef = useRef(null);
  const { pathname } = useLocation();
  const moreIsActive = MORE_ITEMS.some(({ to }) => routeMatches(pathname, to));

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMoreOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        requestAnimationFrame(() => menuButtonRef.current?.focus());
      }
    };
    document.addEventListener('keydown', onKey);
    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    window.dispatchEvent(new Event('bruins09:pause-film'));
    requestAnimationFrame(() => closeButtonRef.current?.focus());
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!moreOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMoreOpen(false);
        requestAnimationFrame(() => moreButtonRef.current?.focus());
      }
    };
    const onPointerDown = (event) => {
      if (!moreDisclosureRef.current?.contains(event.target)) {
        setMoreOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [moreOpen]);

  const navigateFromMenu = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    setMobileOpen(false);
    setMoreOpen(false);
  };

  const closeMenu = () => {
    setMobileOpen(false);
    requestAnimationFrame(() => menuButtonRef.current?.focus());
  };

  const desktopLinkClass = (to, isActive) => [
    'inline-flex items-center gap-1.5 border-b-2 pb-1',
    isActive ? 'border-accent-gold' : 'border-transparent',
    isSpotlight(to) ? 'text-accent-gold' : isActive ? 'text-ink-primary' : 'hover:text-ink-secondary',
  ].join(' ');

  return (
    <>
    <nav className={`chrome ${solid ? 'chrome--solid border-b border-border' : 'chrome--over border-b border-transparent'} px-4 sm:px-6 md:px-9 py-[10px] sm:py-[14px] flex justify-between items-center gap-4`} aria-label="Primary">
      <NavLink to="/" onClick={navigateFromMenu} className="text-lg font-black tracking-[0.24em] sm:tracking-[0.3em] shrink-0">
        BRUINS<span className="text-accent-gold">09</span>
        <span className="hidden sm:inline text-[9px] text-ink-muted tracking-[0.15em] ml-3 font-semibold">CLASS OF '09</span>
      </NavLink>
      <div className="hidden lg:flex flex-nowrap items-center justify-end gap-x-2 xl:gap-x-5 text-[10px] xl:text-[11px] font-extrabold tracking-[0.08em] xl:tracking-[0.16em] text-ink-tertiary whitespace-nowrap min-w-0">
        {PRIMARY_ITEMS.map(({ to, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            onClick={navigateFromMenu}
            className={({ isActive }) => desktopLinkClass(to, isActive)}
          >
            {isSpotlight(to) && <span className="live-dot shrink-0" aria-hidden="true" />}
            {label}
          </NavLink>
        ))}
        <div ref={moreDisclosureRef} className="relative">
          <button
            ref={moreButtonRef}
            type="button"
            className={`inline-flex items-center border-b-2 pb-1 hover:text-ink-secondary ${moreIsActive ? 'border-accent-gold text-ink-primary' : 'border-transparent'}`}
            aria-haspopup="true"
          aria-expanded={moreOpen}
            aria-controls="desktop-more-menu"
            onClick={() => setMoreOpen((current) => !current)}
          >
            MORE <span className="ml-1" aria-hidden="true">▾</span>
          </button>
          {moreOpen && (
            <div
              id="desktop-more-menu"
              className="absolute right-0 top-full mt-3 w-52 border border-border bg-bg-deep/95 p-2 shadow-2xl backdrop-blur"
              aria-label="More destinations"
            >
              {MORE_ITEMS.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={navigateFromMenu}
                  className={({ isActive }) => `block border-l-2 px-3 py-2.5 ${isActive ? 'border-accent-gold bg-accent-gold/10 text-accent-gold' : 'border-transparent text-ink-secondary hover:border-ink-muted hover:bg-bg-surface hover:text-ink-primary'}`}
                >
                  {label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </div>
      <button
        ref={menuButtonRef}
        type="button"
        className="lg:hidden inline-flex min-h-11 items-center gap-2 border border-border px-3 py-2 text-[11px] font-extrabold tracking-[0.18em] text-ink-primary"
        aria-expanded={mobileOpen}
        aria-controls="primary-menu"
        onClick={() => setMobileOpen(true)}
      >
        <span aria-hidden="true" className="inline-block w-4">
          <span className="block h-[2px] bg-current" />
          <span className="mt-1 block h-[2px] bg-current" />
        </span>
        MENU
      </button>
    </nav>
    {mobileOpen && (
      <div id="primary-menu" className="sheet lg:hidden" role="dialog" aria-modal="true" aria-label="Site menu">
        <div className="sheet__bar">
          <div className="text-lg font-black tracking-[0.24em] text-white">
            BRUINS<span className="text-accent-gold">09</span>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="sheet__close"
            onClick={closeMenu}
            aria-label="Close site menu"
          >
            <span aria-hidden="true" className="sheet__close-icon" />
            Close
          </button>
        </div>
        <div className="sheet__scroller">
          <div className="sheet__eyebrow">Browse the almanac</div>
          <div className="sheet__links">
            {ALL_ITEMS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                onClick={navigateFromMenu}
                className={({ isActive }) => (isActive || isSpotlight(to) ? 'is-active' : '')}
              >
                {isSpotlight(to) && <span className="live-dot mr-2 shrink-0" aria-hidden="true" />}
                {label}
              </NavLink>
            ))}
          </div>
          <div className="sheet__footer">Class of '09 · Vol XIV</div>
        </div>
      </div>
    )}
    </>
  );
}
